import { useEffect, useRef, type KeyboardEvent, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import s from "./ui.module.css";

export interface ReiterEintrag<T extends string> {
  id: T;
  label: string;
  /** Zahl hinter dem Namen, zum Beispiel Aufgaben im Backlog. Eine Komponente geht auch (Kapsel-Register, Line-up). */
  zahl?: ReactNode;
  /** Der Reiter öffnet eine eigene Seite (Sitzungen, Sprint). Ohne Pfad schaltet er nur den Inhalt darunter um. */
  pfad?: string;
  /** Eigener Inhalt je Reiter, der immer im Dokument steht (Aufgaben-Dialog am Handy). Sonst gilt `panel` der Leiste. */
  panel?: string;
}

interface Props<T extends string> {
  /** Name der Leiste für Screenreader */
  label: string;
  eintraege: readonly ReiterEintrag<T>[];
  aktiv: T;
  onWechsel?: (id: T) => void;
  /** id des Inhalts darunter (role="tabpanel"), siehe ReiterInhalt */
  panel: string;
  className?: string | undefined;
}

/** id eines Reiters, damit der Inhalt sich darauf beziehen kann (aria-labelledby) */
export const reiterId = (panel: string, id: string) => `${panel}-reiter-${id.replace(/[^\w-]/g, "")}`;

/** Zustand beim Wechsel mit den Pfeiltasten: Die neue Seite gibt dem Reiter den Fokus zurück, die Shell lässt ihn dort. */
export const REITER_FOKUS = { reiterFokus: true } as const;
export const mitReiterFokus = (state: unknown) => !!state && typeof state === "object" && "reiterFokus" in state;

/** Reiter (BAUPLAN-PAKETE.md, Abschnitt 5.4): role="tablist", Pfeiltasten, Pos1 und Ende wechseln, eine orange Linie markiert den
 *  aktiven, dazu fette Schrift (nie nur Farbe). Mindestens 44 px hoch. Umschalter wie Liste und Board bleiben `Segment`. */
export function Reiter<T extends string>({ label, eintraege, aktiv, onWechsel, panel, className }: Props<T>) {
  const navigate = useNavigate();
  const ort = useLocation();
  const knoepfe = useRef(new Map<string, HTMLElement>());

  // Nach einem Wechsel mit der Tastatur auf eine andere Seite: Fokus wieder auf den Reiter, damit es mit den Pfeilen weitergeht
  const tastaturWechsel = mitReiterFokus(ort.state);
  useEffect(() => {
    if (tastaturWechsel) knoepfe.current.get(aktiv)?.focus();
  }, [tastaturWechsel, aktiv]);

  function waehlen(r: ReiterEintrag<T>) {
    if (r.pfad) {
      void navigate(r.pfad, { state: REITER_FOKUS });
      return;
    }
    onWechsel?.(r.id);
    knoepfe.current.get(r.id)?.focus();
  }

  function taste(e: KeyboardEvent, i: number) {
    const n = eintraege.length;
    const ziel = e.key === "ArrowRight" ? (i + 1) % n : e.key === "ArrowLeft" ? (i - 1 + n) % n : e.key === "Home" ? 0 : e.key === "End" ? n - 1 : -1;
    const r = eintraege[ziel];
    if (!r) return;
    e.preventDefault();
    waehlen(r);
  }

  return (
    <div role="tablist" aria-label={label} className={className ? `${s.reiter} ${className}` : s.reiter}>
      {eintraege.map((r, i) => {
        const an = r.id === aktiv;
        const merken = (el: HTMLElement | null) => {
          if (el) knoepfe.current.set(r.id, el);
          else knoepfe.current.delete(r.id);
        };
        const gemeinsam = {
          role: "tab",
          id: reiterId(r.panel ?? panel, r.id),
          "aria-selected": an,
          // Ein Inhalt, der nur für den aktiven Reiter im Dokument steht, gehört nur zu diesem
          "aria-controls": r.panel ?? (an ? panel : undefined),
          tabIndex: an ? 0 : -1,
          onKeyDown: (e: KeyboardEvent) => taste(e, i),
        } as const;
        const inhalt = (
          <>
            {r.label}
            {r.zahl !== undefined && r.zahl !== null ? <span className={s.reiterZahl}>{r.zahl}</span> : null}
          </>
        );
        return r.pfad ? (
          <Link key={r.id} ref={merken} to={r.pfad} {...gemeinsam}>
            {inhalt}
          </Link>
        ) : (
          <button key={r.id} ref={merken} type="button" onClick={() => onWechsel?.(r.id)} {...gemeinsam}>
            {inhalt}
          </button>
        );
      })}
    </div>
  );
}

/** Inhalt unter den Reitern */
export function ReiterInhalt({ panel, aktiv, className, children }: { panel: string; aktiv: string; className?: string; children: ReactNode }) {
  return (
    <div role="tabpanel" id={panel} aria-labelledby={reiterId(panel, aktiv)} className={className}>
      {children}
    </div>
  );
}
